import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Input from '@welldev-group/rcl-input';
import Icon from '@welldev-group/rcl-icon';
import {
  BreadcrumbItem,
  Breadcrumbs,
  I18nText,
  SortingPill,
  CheckBoxFilter as RCLCheckBoxFilter,
} from '@welldev-group/react-comp-lib';
import { debounce } from 'throttle-debounce';
import PaginationWithTranslation from '../../../shared/components/Pagination/PaginationWithTranslation';
import httpClient from '../../../shared/libraries/httpClient';
import routes from '../../../shared/routes';
import ListTable from './ListTable';
import UserModal from './UserModal';
import CheckBoxFilter from '../../../shared/components/Filters/CheckBoxFilter';

const UserIndex = props => {
  const {
    currentUserId,
    permissionTemplateOptions,
    permissionOptions,
    urls,
    displayInvites,
    userAffiliateId,
  } = props;

  const [searchParam, setSearchParam] = useState('');
  const [users, setUsers] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [alphabetical, setAlphabetical] = useState(null);
  const [lastSeen, setLastSeen] = useState(null);

  const [statuses, setStatuses] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [teamIds, setTeamIds] = useState([]);

  const fetchUsers = async (
    page,
    searchParam,
    alphabetical,
    lastSeen,
    statuses,
    teamIds,
  ) => {
    let column;
    let direction;
    let url;

    if (alphabetical) {
      column = 'first_name';
      direction = alphabetical;
    } else if (lastSeen) {
      column = 'last_login';
      direction = lastSeen;
    }

    if (displayInvites) {
      url = routes.users.listInvites;
    } else {
      url = routes.users.list;
    }
    const { data } = await httpClient.get(
      url({
        q: searchParam,
        page,
        affiliate_id: userAffiliateId,
        column,
        direction,
        'state': statuses,
        'teams[]': teamIds,
      }),
    );
    setUsers(prevState => {
      const newState = { ...prevState };
      newState[userAffiliateId] = data.users;
      return newState;
    });
    if (typeof data.pagination !== 'undefined') {
      setCurrentPage(data.pagination.currentPage);
      setTotalPages(data.pagination.totalPages);
    }
  };

  const fetchUsersByAffiliateWithDebounce = useRef(
    debounce(
      500,
      false,
      (
        currentPage,
        searchParam,
        alphabetical,
        lastSeen,
        statuses,
        teamIds,
      ) => {
        fetchUsers(
          currentPage[userAffiliateId],
          searchParam,
          alphabetical,
          lastSeen,
          statuses,
          teamIds,
        );
      },
    ),
  ).current;

  const [teamSearchParam, setTeamSearchParam] = useState('');
  const fetchTeams = debounce(500, false, async inputValue => {
    if (inputValue.length > 1) {
      const { data } = await httpClient.get(
        routes.teams.list({ q: inputValue }),
      );

      const teamOptions = data.teams.map(team => ({
        label: team.name,
        value: team.id,
      }));
      setTeams(teamOptions);
    }
  });

  const teamSearchHandler = (param, value) => {
    setTeamSearchParam(param);
    fetchTeams(value);
  };

  const onTeamsChange = newSelectedTeam => {
    const remainingTeams = [...selectedTeams];

    if (remainingTeams.includes(newSelectedTeam)) {
      const indexOfNewSelectedTeam = selectedTeams.indexOf(newSelectedTeam);
      remainingTeams.splice(indexOfNewSelectedTeam, 1);
    } else {
      remainingTeams.push(newSelectedTeam);
    }

    setSelectedTeams(remainingTeams);
    setTeamIds(remainingTeams.map(team => team.value));
  };

  const setAlphabeticalSortOrder = () => {
    if (alphabetical === null) {
      setAlphabetical('up');
    } else if (alphabetical === 'up') {
      setAlphabetical('down');
    } else {
      setAlphabetical(null);
    }
    setLastSeen(null);
  };

  const setLastSeenSortOrder = () => {
    if (lastSeen === null) {
      setLastSeen('up');
    } else if (lastSeen === 'up') {
      setLastSeen('down');
    } else {
      setLastSeen(null);
    }
    setAlphabetical(null);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchUsersByAffiliateWithDebounce(
      currentPage,
      searchParam,
      alphabetical,
      lastSeen,
      statuses,
      teamIds,
    );
  }, [searchParam, alphabetical, lastSeen, statuses, teamIds]);

  const fetchUsersByAffiliate = () => {
    fetchUsersByAffiliateWithDebounce(
      currentPage,
      searchParam,
      alphabetical,
      lastSeen,
      statuses,
      teamIds,
    );
  };

  const onStatusFilterChange = selectedOption => {
    setStatuses(selectedOption && selectedOption.map(status => status.value));
  };

  const statusOptions = [
    {
      label: <I18nText id="user.list.status.active" />,
      value: 'active',
    },
    {
      label: <I18nText id="user.list.status.deactivated" />,
      value: 'deactivated',
    },
  ];

  const resendInvite = userId =>
    httpClient
      .put(routes.users.resendInvite({ userId }))
      .then(fetchUsersByAffiliate);

  const verifyEmail = userId =>
    httpClient
      .put(routes.users.verifyEmail({ userId }))
      .then(async response => {
        await fetchUsersByAffiliate();
        return response;
      });

  const reverifyEmail = userId =>
    httpClient
      .put(routes.users.reverifyEmail({ userId }))
      .then(async response => {
        await fetchUsersByAffiliate();
        return response;
      });

  const deleteUser = userId =>
    httpClient.delete(routes.users.destroy({ userId })).then(async response => {
      await fetchUsersByAffiliate();
      return response;
    });

  const reactivateUser = userId =>
    httpClient.put(routes.users.reactivate({ userId })).then(async response => {
      await fetchUsersByAffiliate();
      return response;
    });

  return (
    <div className="user-index">
      <div className="user-index__header">
        <div className="user-index__header-text">
          <I18nText id="user.header" />
        </div>
        {displayInvites && (
          <div className="user-index__breadcrumbs">
            <Breadcrumbs>
              <BreadcrumbItem linkUrl={urls.users}>
                <I18nText id="user.header" />
              </BreadcrumbItem>
              <BreadcrumbItem linkUrl={urls.invites}>
                <I18nText id="user.pending_invites" />
              </BreadcrumbItem>
            </Breadcrumbs>
          </div>
        )}
      </div>
      <div className="user-index__search-section">
        <div className="user-index__search-box-container">
          <div className="user-index__search-box">
            <Input
              value={searchParam}
              onChange={value => setSearchParam(value)}
              preIcon={<Icon name="search" />}
              placeholder={I18n.t('components.support.search')}
              fullWidth={true}
            />
          </div>
        </div>
        <div className="user-index__create-actions col-auto col-bleed">
          <a
            className="button button--v2 button--small user-index__create-actions-manage-team-button"
            href={urls.teams}
          >
            <I18nText id="team.manage" />
          </a>
          <a
            className="button button--v2 button--small user-index__create-actions-pending-button"
            href={displayInvites ? urls.users : urls.invites}
          >
            {displayInvites ? (
              <I18nText id="user.active_users" />
            ) : (
              <I18nText id="user.pending_invites" />
            )}
          </a>
          <UserModal
            userAffiliateId={userAffiliateId}
            permissionTemplateOptions={permissionTemplateOptions}
            permissionOptions={permissionOptions}
            fetchUsersByAffiliate={fetchUsersByAffiliate}
            linkText={<I18nText id="user.actions.invite" />}
            title={<I18nText id="user.invite.title" />}
            showPasswordFields={false}
            submitURL={routes.users.invite}
            linkClassName="button button--v2 button--small button--primary"
          />
        </div>
      </div>
      <div className="user-index__sort-section">
        <SortingPill
          size="small"
          sortDirection={alphabetical}
          parameterName={<I18nText id="user.sort_label.name" />}
          onClick={setAlphabeticalSortOrder}
        />
        {!displayInvites && (
          <SortingPill
            size="small"
            sortDirection={lastSeen}
            parameterName={<I18nText id="user.sort_label.last_active" />}
            onClick={setLastSeenSortOrder}
          />
        )}

        {!displayInvites && (
          <div className="user-index__filter-button">
            <CheckBoxFilter
              label={
                <div className="user-index__label-container">
                  <Icon name="highPriority" size="small" />
                  <I18nText id="user.sort_label.status" />
                </div>
              }
              options={statusOptions}
              onChange={onStatusFilterChange}
            />
          </div>
        )}
        {!displayInvites && (
          <div className="user-index__filter-button">
            <RCLCheckBoxFilter
              onChange={onTeamsChange}
              onSearch={(p, e) => teamSearchHandler(p, e)}
              searchParam={teamSearchParam}
              options={teams}
              label={
                <div className="user-index__label-container">
                  <Icon name="approvalRequired" size="small" />
                  <I18nText id="user.sort_label.teams" />
                </div>
              }
              selectedOptions={selectedTeams}
            />
          </div>
        )}
      </div>
      <div className="user-index__list">
        <ListTable
          currentUserId={currentUserId}
          users={users[userAffiliateId] || []}
          affiliateId={userAffiliateId}
          searchParam={searchParam}
          alphabetical={alphabetical}
          lastSeen={lastSeen}
          statuses={statuses}
          teamIds={teamIds}
          currentPage={currentPage[userAffiliateId]}
          totalPages={totalPages[userAffiliateId]}
          setCurrentPage={setCurrentPage}
          verifyEmail={verifyEmail}
          reverifyEmail={reverifyEmail}
          fetchUsers={fetchUsers}
          displayInvites={displayInvites}
          resendInvite={resendInvite}
          deleteUser={deleteUser}
          reactivateUser={reactivateUser}
          fetchUsersByAffiliate={fetchUsersByAffiliate}
          permissionOptions={permissionOptions}
          permissionTemplateOptions={permissionTemplateOptions}
        />
        <PaginationWithTranslation
          currentPage={currentPage}
          totalPages={totalPages}
          onPaginationClick={value => {
            setCurrentPage(value);
            fetchUsers(
              value,
              searchParam,
              alphabetical,
              lastSeen,
              statuses,
              teamIds,
              displayInvites,
            );
          }}
        />
      </div>

    </div>
  );
};

UserIndex.defaultProps = {
  permissionTemplateOptions: [],
  displayInvites: false,
};

UserIndex.propTypes = {
  displayInvites: PropTypes.bool,
  userAffiliateId: PropTypes.number.isRequired,
  urls: PropTypes.shape({
    users: PropTypes.string.isRequired,
    invites: PropTypes.string.isRequired,
    teams: PropTypes.string.isRequired,
  }).isRequired,
  permissionOptions: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string))
    .isRequired,
  permissionTemplateOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  ),
  currentUserId: PropTypes.number.isRequired,
};

export default UserIndex;
